/**
 * Контент-константы для вкладки «Документация» (/admin/api). Вынесены из
 * ApiDocsTab.tsx, чтобы компонент-рендерер не разрастался (правило CLAUDE.md —
 * >400 строк → выносить контент). Примеры сверены с фактическими Zod-схемами
 * (packages/shared/src/schemas/integration.ts, order.ts) и роутами
 * (apps/api/src/modules/public-api/{public-routes,plugin}.ts) на момент написания.
 *
 * Пример счетов/сумм — вымышленный, но формат (коды счетов, масштаб валют,
 * курс RUB≈83.98) соответствует реальным сидам/константам проекта.
 */

export interface FieldDoc {
  field: string;
  type: string;
  required: string;
  description: string;
}

/** Все поля PublicCreateOrderInput (packages/shared/src/schemas/integration.ts), в порядке схемы. */
export const CREATE_ORDER_FIELDS: FieldDoc[] = [
  {
    field: 'client',
    type: 'string (код счёта)',
    required: 'опционально',
    description:
      'Счёт клиента (ноги idx0/idx1). Если не передан — берётся defaults.client с API-ключа; если и там пусто — 400 bad_request.',
  },
  {
    field: 'executor',
    type: 'string (код счёта)',
    required: 'опционально',
    description:
      'Счёт исполнителя (ноги idx2/idx3). Если не передан — берётся defaults.executor с ключа; иначе — 400 bad_request.',
  },
  {
    field: 'clGiveCcy',
    type: 'string (ISO-код валюты)',
    required: 'обязательно',
    description: 'Валюта, которую отдаёт клиент (нога idx0).',
  },
  {
    field: 'clGiveAmt',
    type: 'string, decimal > 0',
    required: 'обязательно',
    description:
      'БАЗОВАЯ сумма клиента — ДО коэффициента. Сервер сам умножает на (1 + coefPct/100) и прибавляет фикс; в ответе это исходное значение сохраняется отдельно как clGiveBaseAmt, а clGiveAmt в ответе — уже итоговая сумма.',
  },
  {
    field: 'coefPct',
    type: 'string, decimal (−100; 100]',
    required: 'опционально',
    description:
      'Наценка/скидка на сумму клиента, транзитом уходящая рефереру (не компании). Применяется к clGiveAmt после расчёта курса. Профит компании считается от базы — коэффициент на него не влияет.',
  },
  {
    field: 'fixUsdt',
    type: 'string, decimal > 0',
    required: 'опционально',
    description:
      'Фикс за выпуск платежа в USDT (транзитом): прибавляется и к ноге клиента, и к ноге исполнителя (в их валютах, по курсу на момент создания). В профит компании не входит — комиссия платится клиентом и доходит до исполнителя 1:1.',
  },
  {
    field: 'bonusUsdt',
    type: 'string, decimal > 0',
    required: 'опционально',
    description:
      'Бонус исполнителю в USDT (благодарность за сделку). Прибавляется ТОЛЬКО к ноге исполнителя (exGet), клиента не касается. Оплачивается из маржи компании — уменьшает нетто-профит.',
  },
  {
    field: 'clGetCcy',
    type: 'string (ISO-код валюты)',
    required: 'обязательно',
    description: 'Валюта, которую получает клиент (нога idx1).',
  },
  {
    field: 'clGetAmt',
    type: 'string, decimal > 0',
    required: 'обязательно',
    description: 'Сумма, которую получает клиент.',
  },
  {
    field: 'clRate',
    type: 'string, decimal | null',
    required: 'опционально',
    description: 'Курс клиента — информационное поле для аудита/UI, в формулу профита напрямую не входит.',
  },
  {
    field: 'exGiveCcy',
    type: 'string (ISO-код валюты)',
    required: 'обязательно',
    description:
      'Валюта, которую отдаёт исполнитель (нога idx3). Обычно совпадает с clGetCcy — исполнитель физически передаёт клиенту то, что тот получает.',
  },
  {
    field: 'exGiveAmt',
    type: 'string, decimal > 0',
    required: 'обязательно',
    description: 'Сумма, которую отдаёт исполнитель.',
  },
  {
    field: 'exGetCcy',
    type: 'string (ISO-код валюты)',
    required: 'обязательно',
    description: 'Валюта, которую получает исполнитель (нога idx2) как компенсацию.',
  },
  {
    field: 'exGetAmt',
    type: 'string, decimal > 0',
    required: 'обязательно',
    description:
      'Сумма, которую получает исполнитель. Разница между базой клиента и этой суммой (обе приведены к USDT по курсам снапшота) формирует профит компании.',
  },
  {
    field: 'exRate',
    type: 'string, decimal | null',
    required: 'опционально',
    description: 'Курс исполнителя — информационное поле, аналогично clRate.',
  },
  {
    field: 'operator',
    type: 'string, 1–64 символа',
    required: 'опционально',
    description:
      'Идентификатор оператора/системы, инициировавшей заявку (для аудита). Приоритет: значение из тела → defaults.operator с ключа → fallback api:<имя ключа>.',
  },
  {
    field: 'date',
    type: 'string, YYYY-MM-DD',
    required: 'опционально',
    description: 'Бизнес-дата заявки. Если не передана — берётся текущая дата сервера (UTC).',
  },
  {
    field: 'rateOverrides',
    type: 'объект { [ISO-код]: decimal > 0 }',
    required: 'опционально',
    description:
      'Фикс курсов USDT именно для этой заявки — перекрывает live-справочник на момент создания. Разрешённые ключи — только clGiveCcy и exGetCcy (валюты формулы профита); USDT переопределить нельзя (всегда 1).',
  },
  {
    field: 'externalId',
    type: 'string, 1–128 символов',
    required: 'опционально',
    description:
      'Идентификатор заявки во внешней системе. Основа идемпотентности — см. раздел «Идемпотентность» ниже.',
  },
  {
    field: 'confirmAll',
    type: 'boolean',
    required: 'опционально',
    description:
      'Авто-подтверждение: если true, все 4 ноги подтверждаются сразу при создании и заявка рождается закрытой (status = closed, все проводки done). Требует у ключа скоуп orders:confirm — иначе 403, и заявка НЕ создаётся. Игнорируется при идемпотентном повторе (заявка с этим externalId уже создана — возвращается как есть, 200). Если настроены вебхуки, придут 4× provodka.confirmed и итоговый status.changed.',
  },
];

export interface ErrorDoc {
  code: string;
  http: string;
  description: string;
}

export const ERROR_CODES: ErrorDoc[] = [
  {
    code: 'validation_error',
    http: '400',
    description:
      'Тело/параметры запроса не прошли Zod-схему (например, не тот формат суммы или отсутствует обязательное поле). details — массив ошибок, по одной записи на поле.',
  },
  {
    code: 'bad_request',
    http: '400',
    description:
      'Бизнес-предпроверка не прошла: счёт или валюта не найдены в справочнике, нет курса USDT для расчёта профита, client/executor не переданы и не заданы на ключе.',
  },
  {
    code: 'unauthorized',
    http: '401',
    description:
      'Заголовок Authorization отсутствует, не Bearer, ключ не найден, отозван или выключен. Причина намеренно не детализируется.',
  },
  {
    code: 'forbidden',
    http: '403',
    description: 'У ключа нет нужного скоупа для этого эндпоинта.',
  },
  {
    code: 'not_found',
    http: '404',
    description:
      'Заявка не найдена или принадлежит другому ключу (чужие заявки намеренно отдаются как 404, а не 403).',
  },
  {
    code: 'conflict',
    http: '409',
    description: 'Конфликт состояния (зарезервировано; в MVP публичного API штатно не возникает).',
  },
];

/** Что даёт каждый скоуп ключа (см. API_KEY_SCOPES в shared). */
export const SCOPE_ACCESS: Record<string, string> = {
  'orders:create': 'POST /orders — создание заявок',
  'orders:read': 'GET /orders/:id, GET /orders/by-external/:externalId',
  'orders:confirm': 'POST /orders с confirmAll:true — авто-подтверждение проводок (дополнительно к orders:create)',
  'refs:read': 'GET /ping, /accounts, /currencies, /usdt-rates',
};

/** Расшифровка события вебхука — для строки таблицы (лейбл берётся из WEBHOOK_EVENT_LABELS). */
export const WEBHOOK_EVENT_DESCRIPTIONS: Record<string, string> = {
  'order.status.changed': 'любое изменение статуса заявки (в т.ч. отклонение, удаление, итоговое подтверждение).',
  'order.provodka.confirmed': 'подтверждена одна из четырёх ног заявки.',
};

export interface HeaderDoc {
  header: string;
  description: string;
}

export const WEBHOOK_HEADERS: HeaderDoc[] = [
  { header: 'X-Wave-Event', description: 'Тип события, совпадает с полем event тела.' },
  { header: 'X-Wave-Delivery', description: 'Числовой ID доставки (строкой), совпадает с deliveryId тела.' },
  {
    header: 'X-Wave-Timestamp',
    description: 'Unix-время формирования запроса в секундах (строкой) — участвует в подписи как есть.',
  },
  {
    header: 'X-Wave-Signature',
    description:
      "sha256=hex(hmac_sha256(secret, timestamp + '.' + rawBody)) — secret это webhook-секрет ключа (показывается один раз в модалке, можно перегенерировать).",
  },
];

const AUTH_HEADER = 'Authorization: Bearer wave_sk_live_9f3a1c7de2b84f0a9c02e';

export const pingCurl = (base: string): string => `curl -s ${base}/ping \\
  -H "${AUTH_HEADER}"`;

export const pingResponseJson = `{
  "data": {
    "pong": true,
    "key": { "name": "CRM Integration", "scopes": ["orders:create", "orders:read", "refs:read"] }
  }
}`;

export const createOrderCurl = (base: string): string => `curl -s -X POST ${base}/orders \\
  -H "${AUTH_HEADER}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "client": "KSAN-0143",
    "executor": "SELH-0110",
    "clGiveCcy": "RUB",
    "clGiveAmt": "100000.00",
    "clGetCcy": "USDT",
    "clGetAmt": "1180.00",
    "clRate": "84.75",
    "exGiveCcy": "USDT",
    "exGiveAmt": "1180.00",
    "exGetCcy": "RUB",
    "exGetAmt": "97000.00",
    "exRate": "82.2",
    "date": "2026-07-03",
    "externalId": "crm-order-88213"
  }'`;

export const createOrderResponseJson = `{
  "data": {
    "id": "W-10482",
    "client": "KSAN-0143",
    "executor": "SELH-0110",
    "clGiveCcy": "RUB",
    "clGiveAmt": "100000.00",
    "clGetCcy": "USDT",
    "clGetAmt": "1180.00",
    "date": "2026-07-03",
    "statusFlag": "active",
    "status": "new",
    "progress": 0,
    "rejectReason": null,
    "clRate": "84.75",
    "coefPct": null,
    "clGiveBaseAmt": "100000.00",
    "fixUsdt": null,
    "fixClAmt": null,
    "fixExAmt": null,
    "bonusUsdt": null,
    "bonusExAmt": null,
    "exGiveCcy": "USDT",
    "exGiveAmt": "1180.00",
    "exGetCcy": "RUB",
    "exGetAmt": "97000.00",
    "exRate": "82.2",
    "provodki": [
      { "idx": 0, "account": "KSAN-0143", "ccy": "RUB", "amount": "-100000.00", "status": "pending", "confirmedAt": null },
      { "idx": 1, "account": "KSAN-0143", "ccy": "USDT", "amount": "1180.00", "status": "pending", "confirmedAt": null },
      { "idx": 2, "account": "SELH-0110", "ccy": "RUB", "amount": "97000.00", "status": "pending", "confirmedAt": null },
      { "idx": 3, "account": "SELH-0110", "ccy": "USDT", "amount": "-1180.00", "status": "pending", "confirmedAt": null }
    ],
    "createdAt": "2026-07-03T09:14:02.118Z",
    "updatedAt": "2026-07-03T09:14:02.118Z",
    "externalId": "crm-order-88213"
  }
}`;

export const idempotentRetryCurl = (base: string): string => `# точно тот же запрос, тот же externalId — ключ уже создавал такую заявку
curl -s -i -X POST ${base}/orders \\
  -H "${AUTH_HEADER}" \\
  -H "Content-Type: application/json" \\
  -d '{ "...": "тело как в первом вызове", "externalId": "crm-order-88213" }'

# HTTP/1.1 200 OK  (не 201 — заявка не создаётся повторно)
# тело — та же заявка "W-10482", что и при первом вызове`;

export const getOrderCurl = (base: string): string => `curl -s ${base}/orders/W-10482 \\
  -H "${AUTH_HEADER}"`;

export const getOrderByExternalCurl = (base: string): string => `curl -s ${base}/orders/by-external/crm-order-88213 \\
  -H "${AUTH_HEADER}"`;

export const orderNotFoundJson = `{
  "error": {
    "code": "not_found",
    "message": "order with externalId crm-order-88213 not found",
    "details": null
  }
}`;

export const accountsCurl = (base: string): string => `curl -s ${base}/accounts \\
  -H "${AUTH_HEADER}"`;

export const accountsResponseJson = `{
  "data": [
    { "code": "KSAN-0143", "name": null, "telegramChatId": "-1001234567890" },
    { "code": "SELH-0110", "name": "P2P-исполнитель Beta", "telegramChatId": null }
  ]
}`;

export const currenciesCurl = (base: string): string => `curl -s ${base}/currencies \\
  -H "${AUTH_HEADER}"`;

export const currenciesResponseJson = `{
  "data": [
    { "code": "RUB", "symbol": "₽", "scale": 2 },
    { "code": "USDT", "symbol": "$", "scale": 8 },
    { "code": "USD", "symbol": "$", "scale": 2 },
    { "code": "EUR", "symbol": "€", "scale": 2 }
  ]
}`;

export const usdtRatesCurl = (base: string): string => `curl -s ${base}/usdt-rates \\
  -H "${AUTH_HEADER}"`;

export const usdtRatesResponseJson = `{
  "data": [
    { "ccy": "RUB", "rate": "83.98", "updatedAt": "2026-07-03T06:00:00.000Z" },
    { "ccy": "USD", "rate": "1", "updatedAt": "2026-07-03T06:00:00.000Z" },
    { "ccy": "EUR", "rate": "0.98", "updatedAt": "2026-07-03T06:00:00.000Z" }
  ]
}`;

export const validationErrorJson = `{
  "error": {
    "code": "validation_error",
    "message": "Request validation failed",
    "details": [
      {
        "instancePath": "/clGiveAmt",
        "message": "must be a decimal string with up to 4 fraction digits",
        "params": { "issue": { "code": "invalid_string", "path": ["clGiveAmt"] } }
      }
    ]
  }
}`;

export const badRequestErrorJson = `{
  "error": {
    "code": "bad_request",
    "message": "счёт не найден в справочнике: SELH-9999",
    "details": null
  }
}`;

export const rateLimitErrorJson = `HTTP/1.1 429 Too Many Requests

{ "error": { "code": "Error", "message": "Rate limit exceeded, retry in 1 minute" } }`;

export const webhookPayloadJson = `{
  "event": "order.provodka.confirmed",
  "deliveryId": 42,
  "orderId": "W-10482",
  "externalId": "crm-order-88213",
  "occurredAt": "2026-07-03T09:31:44.207Z",
  "data": {
    "id": "W-10482",
    "client": "KSAN-0143",
    "executor": "SELH-0110",
    "clGiveCcy": "RUB",
    "clGiveAmt": "100000.00",
    "clGetCcy": "USDT",
    "clGetAmt": "1180.00",
    "date": "2026-07-03",
    "statusFlag": "active",
    "status": "progress",
    "progress": 1,
    "rejectReason": null,
    "clRate": "84.75",
    "coefPct": null,
    "clGiveBaseAmt": "100000.00",
    "fixUsdt": null,
    "fixClAmt": null,
    "fixExAmt": null,
    "bonusUsdt": null,
    "bonusExAmt": null,
    "exGiveCcy": "USDT",
    "exGiveAmt": "1180.00",
    "exGetCcy": "RUB",
    "exGetAmt": "97000.00",
    "exRate": "82.2",
    "provodki": [
      { "idx": 0, "account": "KSAN-0143", "ccy": "RUB", "amount": "-100000.00", "status": "done", "confirmedAt": "2026-07-03T09:31:44.207Z" },
      { "idx": 1, "account": "KSAN-0143", "ccy": "USDT", "amount": "1180.00", "status": "pending", "confirmedAt": null },
      { "idx": 2, "account": "SELH-0110", "ccy": "RUB", "amount": "97000.00", "status": "pending", "confirmedAt": null },
      { "idx": 3, "account": "SELH-0110", "ccy": "USDT", "amount": "-1180.00", "status": "pending", "confirmedAt": null }
    ],
    "createdAt": "2026-07-03T09:14:02.118Z",
    "updatedAt": "2026-07-03T09:31:44.207Z",
    "externalId": "crm-order-88213"
  }
}`;

export const webhookVerifyNodeJs = `const crypto = require('crypto');

// rawBody — ИМЕННО сырое тело запроса (Buffer/строка как получено по сети),
// а не JSON.parse(...) с последующей пересборкой — иначе изменится порядок
// ключей/пробелы и подпись не совпадёт. Например, с express: app.use(express.raw({ type: '*/*' })).
function verifyWaveSignature(rawBody, headers, webhookSecret) {
  const timestamp = headers['x-wave-timestamp'];
  const received = headers['x-wave-signature']; // формат: "sha256=<hex>"

  const expectedHex = crypto
    .createHmac('sha256', webhookSecret)
    .update(\`\${timestamp}.\${rawBody}\`)
    .digest('hex');
  const expected = \`sha256=\${expectedHex}\`;

  const a = Buffer.from(received);
  const b = Buffer.from(expected);
  if (a.length !== b.length || !crypto.timingSafeEqual(a, b)) {
    throw new Error('invalid webhook signature');
  }
}

app.post('/webhooks/wave', (req, res) => {
  verifyWaveSignature(req.body, req.headers, process.env.WAVE_WEBHOOK_SECRET);
  const event = JSON.parse(req.body);
  // event.deliveryId — используйте для дедупликации (доставка at-least-once,
  // повторные попытки при таймауте/5xx возможны даже после вашего успешного 2xx).
  res.sendStatus(200);
});`;
